import React, { useState } from 'react';
import { Insight, InsightSeverity, InsightType } from '../services/api';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  DollarSign, 
  ThumbsUp, 
  ThumbsDown,
  Sparkles,
  Repeat,
  Check
} from 'lucide-react';

interface InsightCardProps {
  insight: Insight;
  onVote: (id: string, vote: 1 | -1) => void;
  compact?: boolean;
  truncate?: boolean;
}

const getInsightIcon = (type: InsightType, severity: InsightSeverity) => {
  const iconClass = "h-4 w-4";
  
  switch (type) {
    case 'spending_fluctuation':
      return <TrendingUp className={iconClass} />;
    case 'spending_forecast':
      return severity === 'warning' ? <TrendingUp className={iconClass} /> : <TrendingDown className={iconClass} />;
    case 'overdraft_alert':
      return <AlertTriangle className={iconClass} />;
    case 'vendor_increase':
    case 'subscription_increase':
      return <DollarSign className={iconClass} />;
    case 'subscription_spending':
      return <Repeat className={iconClass} />;
    default:
      return <Sparkles className={iconClass} />;
  }
};

// Theme-driven styling (uses the user's selected theme CSS variables)
const getThemeConfig = () => {
  const primary = 'var(--theme-primary)';
  return {
    gradient: `linear-gradient(135deg, color-mix(in srgb, ${primary} 10%, transparent) 0%, color-mix(in srgb, ${primary} 3%, transparent) 100%)`,
    borderColor: `color-mix(in srgb, ${primary} 22%, transparent)`,
    iconBg: `color-mix(in srgb, ${primary} 14%, transparent)`,
    iconColor: primary,
    accentColor: primary,
    badgeBg: `color-mix(in srgb, ${primary} 10%, transparent)`,
  };
};

const formatTimeAgo = (dateString: string | null): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  onVote,
  compact = false,
  truncate
}) => {
  const config = getThemeConfig();
  const [isExiting, setIsExiting] = useState(false);
  const [votedType, setVotedType] = useState<'up' | 'down' | null>(null);
  const shouldTruncate = truncate ?? compact;
  
  const handleVote = (vote: 1 | -1) => {
    setVotedType(vote === 1 ? 'up' : 'down');
    setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onVote(insight.id, vote), 300);
    }, 400);
  };
  
  return (
    <div 
      className={`
        apple-card !p-0 relative overflow-hidden
        ${compact ? 'min-w-[300px] max-w-[340px]' : 'w-full'}
        rounded-2xl
        transition-all duration-300 ease-out
        hover:shadow-lg hover:-translate-y-1
        group
        ${isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
      `}
    >
      {/* Subtle shine effect on hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)'
        }}
      />
      
      <div className={`relative ${compact ? 'p-4' : 'p-5'}`}>
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          {/* Icon */}
          <div 
            className="p-2.5 rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
            style={{ backgroundColor: config.iconBg }}
          >
            <div style={{ color: config.iconColor }}>
              {getInsightIcon(insight.insight_type, insight.severity)}
            </div>
          </div>
          
          {/* Title and time */}
          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold text-sm leading-tight ${shouldTruncate ? 'line-clamp-2' : ''}`}
              style={{ color: 'var(--theme-text)' }}
            >
              {insight.title}
            </h3>
            <span
              className="text-[10px] mt-0.5 block"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              {formatTimeAgo(insight.generated_at)}
            </span>
          </div>
        </div>
        
        {/* Message */}
        <p
          className={`text-sm leading-relaxed ${shouldTruncate ? 'line-clamp-2' : ''} mb-4`}
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          {insight.message}
        </p>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-black/5">
          {/* Type badge */}
          <span 
            className="text-[10px] font-medium px-2 py-1 rounded-full capitalize"
            style={{ 
              backgroundColor: config.badgeBg,
              color: config.accentColor 
            }}
          >
            {insight.insight_type.replace(/_/g, ' ')}
          </span>
          
          {/* Vote buttons */}
          <div className="flex items-center gap-1">
            {votedType === 'up' ? (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-600">
                <Check className="h-3 w-3" />
                <span className="text-[10px] font-medium">Thanks!</span>
              </div>
            ) : votedType === 'down' ? (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                <Check className="h-3 w-3" />
                <span className="text-[10px] font-medium">Noted</span>
              </div>
            ) : (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVote(1);
                  }}
                  className="p-2 rounded-full transition-all duration-200
                           hover:bg-emerald-100 hover:text-emerald-600
                           active:scale-90"
                  title="Helpful"
                >
                  <ThumbsUp className="h-3.5 w-3.5 text-gray-400 hover:text-emerald-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVote(-1);
                  }}
                  className="p-2 rounded-full transition-all duration-200
                           hover:bg-rose-100 hover:text-rose-600
                           active:scale-90"
                  title="Not helpful"
                >
                  <ThumbsDown className="h-3.5 w-3.5 text-gray-400 hover:text-rose-600" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightCard;
